import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { MasterEmpresaService } from '../../../services/master/master-empresa.service';
import { MasterUsuarioGlobalDTO } from '../../../models/master/master-empresa.model';
import { TableColumn } from '../../../models/shared/table-column.model';

/**
 * Area Master — Usuarios.
 *
 * Busca que ATRAVESSA empresas: o caminho de quem so tem o contato de quem ligou. O destino
 * e o mesmo da tela de Empresas (`/master/empresas`) — a ficha da empresa do usuario.
 *
 * Nada muda no usuario logado: o master continua na propria empresa e apenas navega.
 */
@Component({
  selector: 'app-master-usuarios',
  templateUrl: './master-usuarios.component.html',
  styleUrls: ['../master-listagem.scss'],
  providers: [MessageService],
})
export class MasterUsuariosComponent implements OnInit, OnDestroy {
  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  tooltips: any = {};
  usuariosTexts: any = {};

  usuarios: MasterUsuarioGlobalDTO[] = [];
  usuariosLoading = true;
  usuariosFirst = 0;
  usuariosRows = 10;
  usuariosTotal = 0;
  filtroUsuario = '';
  usuariosLastfield = 'id';
  usuariosLastSort: 'asc' | 'desc' = 'desc';

  readonly STORAGE_KEY_USUARIOS = 'master-usuarios-visible-columns';

  colunasUsuarios: TableColumn[] = [
    { key: 'nome', labelKey: 'master.empresas.labels.usuario', visible: true, fixed: true, defaultVisible: true },
    { key: 'email', labelKey: 'master.empresas.labels.email', visible: true, fixed: false, defaultVisible: true },
    { key: 'empresa', labelKey: 'master.empresas.labels.empresa', visible: true, fixed: false, defaultVisible: true },
    { key: 'situacao', labelKey: 'master.empresas.labels.situacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'master.empresas.labels.data_inclusao', visible: false, fixed: false, defaultVisible: false },
    { key: 'acoes', labelKey: 'master.empresas.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  private searchUsuarios$ = new Subject<string>();
  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private masterEmpresaService: MasterEmpresaService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.carregarPreferenciasDeColunas();
    this.setupSearchDebounce();
    this.findUsuarios();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('master.empresas.texts.titulo_usuarios'),
      subtitulo: this.translate.instant('master.empresas.texts.subtitulo_usuarios'),
    };

    this.labels = {
      email: this.translate.instant('master.empresas.labels.email'),
      acoes: this.translate.instant('master.empresas.labels.acoes'),
      itens_por_pagina: this.translate.instant('master.empresas.labels.itens_por_pagina'),
      data_inclusao: this.translate.instant('master.empresas.labels.data_inclusao'),
    };

    this.tooltips = {
      abrir_empresa_do_usuario: this.translate.instant('master.empresas.tooltips.abrir_empresa_do_usuario'),
    };

    this.usuariosTexts = {
      titulo: this.translate.instant('master.empresas.texts.tabela_de_usuarios'),
      nenhum: this.translate.instant('master.empresas.texts.nenhum_usuario'),
      dica_busca: this.translate.instant('master.empresas.texts.dica_busca_usuario'),
      carregando: this.translate.instant('master.empresas.texts.carregando_usuarios'),
      pesquisar: this.translate.instant('master.empresas.placeholders.pesquisar_usuario'),
      usuario: this.translate.instant('master.empresas.labels.usuario'),
      empresa: this.translate.instant('master.empresas.labels.empresa'),
      situacao: this.translate.instant('master.empresas.labels.situacao'),
      ativo: this.translate.instant('master.empresas.labels.ativo'),
      inativo: this.translate.instant('master.empresas.labels.inativo'),
      master: this.translate.instant('master.empresas.labels.master'),
    };
  }

  // =========================================================================
  // Colunas (mesmo contrato do ColumnSelectorComponent das demais listagens)
  // =========================================================================

  isColunaUsuarioVisivel(key: string): boolean {
    return this.colunasUsuarios.find((c) => c.key === key)?.visible ?? true;
  }

  get colunasUsuariosVisiveis(): number {
    return this.colunasUsuarios.filter((c) => c.visible).length;
  }

  onColunasUsuariosChange(colunas: TableColumn[]): void {
    this.colunasUsuarios = [...colunas];
  }

  /** Restaura o que o usuario escolheu da ultima vez (mesmo formato das outras listagens). */
  private carregarPreferenciasDeColunas(): void {
    try {
      const salvo = localStorage.getItem(this.STORAGE_KEY_USUARIOS);
      if (!salvo) return;
      const visiveis = JSON.parse(salvo) as string[];
      this.colunasUsuarios.forEach((col) => {
        if (!col.fixed) {
          col.visible = visiveis.includes(col.key);
        }
      });
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  // =========================================================================
  // Busca
  // =========================================================================

  private setupSearchDebounce(): void {
    this.searchUsuarios$
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(() => {
        this.usuariosFirst = 0;
        this.findUsuarios(0);
      });
  }

  onSearchUsuarioChange(valor: string): void {
    this.filtroUsuario = valor || '';
    this.searchUsuarios$.next(this.filtroUsuario);
  }

  // =========================================================================
  // Usuarios (busca global)
  // =========================================================================

  findUsuarios(page: number = 0, size: number = this.usuariosRows): void {
    this.usuariosLoading = true;
    const sort = `${this.usuariosLastfield},${this.usuariosLastSort}`;
    this.masterEmpresaService.buscarUsuarios(this.filtroUsuario, page, size, sort).subscribe({
      next: (resposta) => {
        this.usuarios = resposta.content || [];
        this.usuariosTotal = resposta.totalElements || 0;
        this.usuariosFirst = page * size;
        this.usuariosLoading = false;
      },
      error: () => {
        this.usuarios = [];
        this.usuariosTotal = 0;
        this.usuariosLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('master.empresas.mensagens.erro_carregar_usuarios'),
        });
      },
    });
  }

  onUsuariosPageChange(evento: any): void {
    this.usuariosRows = evento.rows;
    this.findUsuarios(evento.page, evento.rows);
  }

  onUsuariosRowsPerPageChange(): void {
    this.usuariosFirst = 0;
    this.findUsuarios(0, this.usuariosRows);
  }

  onSortUsuarios(evento: SortEvent): void {
    const campo = evento.field || this.usuariosLastfield;
    const ordem: 'asc' | 'desc' = evento.order === -1 ? 'desc' : 'asc';
    if (campo === this.usuariosLastfield && ordem === this.usuariosLastSort) return;
    this.usuariosLastfield = campo;
    this.usuariosLastSort = ordem;
    this.usuariosFirst = 0;
    this.findUsuarios(0);
  }

  /** Do usuario para a ficha da empresa dele — o motivo desta tela existir. */
  abrirEmpresaDoUsuario(usuario: MasterUsuarioGlobalDTO): void {
    if (usuario?.empresaId == null) return;
    this.router.navigate(['/master/empresas', usuario.empresaId], {
      queryParams: { origem: 'usuarios' },
    });
  }
}
